/**
 * Onboarding Feedback Event Tracking Module
 *
 * Provides comprehensive analytics tracking for the onboarding feedback dialog journey.
 * Tracks user responses, AI report builder redirects, and support chat interactions.
 *
 * @module onboardingFeedbackEvents
 *
 * @example
 * ```typescript
 * import { OnboardingFeedbackEventHandler } from '@/modules/onboardingFeedbackEvents';
 *
 * // Initialize tracker
 * const tracker = new OnboardingFeedbackEventHandler();
 *
 * // Track dialog view
 * tracker.trackDialogViewed('dialog');
 *
 * // Track user response
 * tracker.trackRightReportResponse(false);
 * tracker.trackAIBuilderRedirect();
 * ```
 */

import mixpanel from 'mixpanel-browser';

/**
 * Display type for the feedback dialog
 */
export type DisplayType = 'dialog' | 'appBanner';

/**
 * Reason for opening support chat
 */
export type ChatReason = 'wrong_data' | 'other';

/**
 * Onboarding Feedback Event Handler
 *
 * Manages all analytics tracking for the onboarding feedback dialog journey.
 * Provides methods for tracking user interactions and responses.
 */
export class OnboardingFeedbackEventHandler {
  private sessionId: string;
  private sessionStartTime: number;
  private lastActivityTime: number;

  // Track user responses
  private foundRightReport: boolean | null = null;
  private hasCorrectData: boolean | null = null;
  private redirectedToAI: boolean = false;
  private chatOpened: boolean = false;
  private chatReason: ChatReason | null = null;

  /**
   * Initialize the onboarding feedback event tracker
   */
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();
  }

  // ============================================================================
  // CORE FEEDBACK TRACKING
  // ============================================================================

  /**
   * Track feedback dialog view
   *
   * First touchpoint in the feedback funnel. Records when dialog appears.
   *
   * @param displayType - How the dialog is displayed ('dialog' or 'appBanner')
   *
   * @example
   * ```typescript
   * tracker.trackDialogViewed('dialog');
   * ```
   */
  public trackDialogViewed(displayType: DisplayType): void {
    this.track('Onboarding Feedback Dialog Viewed', {
      sessionId: this.sessionId,
      displayType: displayType,
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track "Right Report" question response
   *
   * Records when user answers "Did you find the right report from Assisty?"
   *
   * @param foundReport - True if user found the right report, false otherwise
   *
   * @example
   * ```typescript
   * tracker.trackRightReportResponse(true);  // User found the right report
   * tracker.trackRightReportResponse(false); // User did not find the right report
   * ```
   */
  public trackRightReportResponse(foundReport: boolean): void {
    this.foundRightReport = foundReport;

    this.track('Onboarding Feedback - Right Report Response', {
      sessionId: this.sessionId,
      foundRightReport: foundReport,
      timeToResponse: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track redirect to AI Report Builder
   *
   * Records when user is redirected to AI report builder page.
   * This happens when user indicates they didn't find the right report.
   *
   * @example
   * ```typescript
   * tracker.trackAIBuilderRedirect();
   * ```
   */
  public trackAIBuilderRedirect(): void {
    this.redirectedToAI = true;

    this.track('Onboarding Feedback - AI Builder Redirected', {
      sessionId: this.sessionId,
      foundRightReport: this.foundRightReport,
      timeToRedirect: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track user chose to redirect later
   *
   * Records when user chooses "Do Later" instead of immediately redirecting
   * to the AI Report Builder.
   *
   * @example
   * ```typescript
   * tracker.trackRedirectLater();
   * ```
   */
  public trackRedirectLater(): void {
    this.track('Onboarding Feedback - Redirect Later', {
      sessionId: this.sessionId,
      foundRightReport: this.foundRightReport,
      timeToResponse: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track "Correct Data" question response
   *
   * Records when user answers "Does the report provided by Assisty show the correct data?"
   *
   * @param hasCorrectData - True if data is correct, false otherwise
   *
   * @example
   * ```typescript
   * tracker.trackCorrectDataResponse(true);  // Data is correct
   * tracker.trackCorrectDataResponse(false); // Data is incorrect
   * ```
   */
  public trackCorrectDataResponse(hasCorrectData: boolean): void {
    this.hasCorrectData = hasCorrectData;

    this.track('Onboarding Feedback - Correct Data Response', {
      sessionId: this.sessionId,
      hasCorrectData: hasCorrectData,
      foundRightReport: this.foundRightReport,
      timeToResponse: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track support chat opened
   *
   * Records when Crisp chat is opened for user support.
   *
   * @param reason - Reason for opening chat ('wrong_data' or 'other')
   * @param message - Optional message sent to support
   *
   * @example
   * ```typescript
   * tracker.trackSupportChatOpened('wrong_data', 'Sales data is incorrect');
   * tracker.trackSupportChatOpened('other', 'Need help with reports');
   * ```
   */
  public trackSupportChatOpened(reason: ChatReason, message?: string): void {
    this.chatOpened = true;
    this.chatReason = reason;

    this.track('Onboarding Feedback - Support Chat Opened', {
      sessionId: this.sessionId,
      reason: reason,
      chatMessage: message || null,
      foundRightReport: this.foundRightReport,
      hasCorrectData: this.hasCorrectData,
      timeOnPage: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track feedback flow completion
   *
   * Records when user completes the full feedback flow.
   * Should be called after all feedback questions are answered.
   *
   * @example
   * ```typescript
   * tracker.trackFeedbackCompleted();
   * ```
   */
  public trackFeedbackCompleted(): void {
    this.track('Onboarding Feedback - Completed', {
      sessionId: this.sessionId,
      // User responses
      foundRightReport: this.foundRightReport,
      hasCorrectData: this.hasCorrectData,
      redirectedToAI: this.redirectedToAI,
      chatOpened: this.chatOpened,
      chatReason: this.chatReason,
      // Completion metrics
      totalTime: this.getTimeOnPage(),
      completedSuccessfully: this.foundRightReport !== null,
      needsSupport: this.chatOpened || !this.foundRightReport,
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Send event to Mixpanel
   *
   * Wrapper around mixpanel.track() with error handling.
   *
   * @param eventName - Name of the event
   * @param properties - Event properties
   * @public
   */
  public track(eventName: string, properties: any): void {
    try {
      mixpanel.track(eventName, properties);
    } catch (error) {
      console.error(
        `[OnboardingFeedbackEvents] Error tracking "${eventName}":`,
        error
      );
    }
  }

  /**
   * Get or create session ID
   *
   * Creates a unique session ID for this feedback dialog session.
   * Stored in sessionStorage to persist across page reloads.
   *
   * @returns Session ID
   * @private
   */
  private getOrCreateSessionId(): string {
    const storageKey = 'onboarding_feedback_session_id';
    let sessionId = sessionStorage.getItem(storageKey);

    if (!sessionId) {
      // Generate session ID using same pattern as other event trackers
      sessionId = `onboarding_feedback_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      sessionStorage.setItem(storageKey, sessionId);
    }

    return sessionId as string;
  }

  /**
   * Calculate time spent in feedback flow
   *
   * @returns Time in seconds since session start
   * @private
   */
  private getTimeOnPage(): number {
    return Math.round((Date.now() - this.sessionStartTime) / 1000);
  }

  /**
   * Update last activity timestamp
   *
   * @private
   */
  private updateLastActivity(): void {
    this.lastActivityTime = Date.now();
  }

  /**
   * Get current session state
   *
   * Utility method for debugging and testing.
   *
   * @returns Current session state
   */
  public getSessionState(): {
    sessionId: string;
    foundRightReport: boolean | null;
    hasCorrectData: boolean | null;
    redirectedToAI: boolean;
    chatOpened: boolean;
    chatReason: ChatReason | null;
    timeOnPage: number;
  } {
    return {
      sessionId: this.sessionId,
      foundRightReport: this.foundRightReport,
      hasCorrectData: this.hasCorrectData,
      redirectedToAI: this.redirectedToAI,
      chatOpened: this.chatOpened,
      chatReason: this.chatReason,
      timeOnPage: this.getTimeOnPage(),
    };
  }

  /**
   * Reset session state
   *
   * Clears all tracked responses. Useful for testing or multi-dialog scenarios.
   */
  public resetSession(): void {
    this.foundRightReport = null;
    this.hasCorrectData = null;
    this.redirectedToAI = false;
    this.chatOpened = false;
    this.chatReason = null;
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();
  }
}
